import { useCallback, useState } from 'react';
import { TableProperties } from 'lucide-react';
import { periodReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS, fmtDate, Section, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pill, CountChip, Dash, InfoBanner, errMessage } from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type { FsMappingReportDto, FsMappingRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'FS_MAPPING';

const EXCEPTION_TONES: Record<string, PillTone> = {
  UNMAPPED:           'red',
  INACTIVE_MAPPED:    'amber',
  DUPLICATE_SEQUENCE: 'amber',
};

const EXCEPTION_LABELS: Record<string, string> = {
  UNMAPPED:           'Unmapped',
  INACTIVE_MAPPED:    'Inactive but mapped',
  DUPLICATE_SEQUENCE: 'Duplicate sequence',
};

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Code',     header: 'Account Code',     width: 14 },
  { key: 'Name',     header: 'Account Name',     width: 32 },
  { key: 'Type',     header: 'Account Type',     width: 14 },
  { key: 'Posting',  header: 'Posting',          width: 10 },
  { key: 'FsGroup',  header: 'FS Group',         width: 20 },
  { key: 'RptGroup', header: 'Reporting Group',  width: 20 },
  { key: 'Seq',      header: 'Sequence',         width: 10 },
  { key: 'Ifrs',     header: 'IFRS Code',        width: 14 },
  { key: 'CashFlow', header: 'Cash-Flow Cat.',   width: 18 },
  { key: 'Active',   header: 'Active',           width: 10 },
];

function buildExportRows(rows: FsMappingRow[]): Record<string, unknown>[] {
  return rows.map(a => ({
    Code:     a.code,
    Name:     a.name,
    Type:     a.accountType,
    Posting:  a.isPostingAccount ? 'Yes' : 'No',
    FsGroup:  a.financialStatementGroup ?? '',
    RptGroup: a.reportingGroup ?? '',
    Seq:      a.reportingSequence ?? '',
    Ifrs:     a.ifrsMappingCode ?? '',
    CashFlow: a.cashFlowCategory ?? '',
    Active:   a.isActive ? 'Yes' : 'No',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const FsMappingPage = () => {
  const { hasPermission } = usePermission();

  const [data, setData] = useState<FsMappingReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await periodReportApi.getFsMapping();
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  if (!hasPermission(Permissions.RPT_FS_MAPPING)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const accounts = data?.accounts ?? [];
  const exceptions = data?.exceptions ?? [];
  const countOf = (type: string) => exceptions.filter(e => e.type === type).length;

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'FS Mapping',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(accounts),
      title: 'Financial Statement Mapping',
      subtitle: data.companyName,
    }, 'FsMapping');
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(accounts), 'FsMapping');
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TableProperties size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Financial Statement Mapping</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={accounts.length}
          filterSummary="full chart"
        />
      </div>

      {/* Run panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
            Reviews how every account maps to financial-statement and reporting groups, and flags
            unmapped, inactive-but-mapped, and duplicate-sequence accounts.
          </p>
          <RunReportButton onClick={() => { void run(); }} isLoading={loading} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Click "Run Report" to audit the chart of accounts against its financial-statement mapping.' />
      )}

      {data && (
        <div className="flex flex-col gap-4">
          {/* Exceptions first */}
          <Section
            title="Mapping Exceptions"
            subtitle={`${data.companyName} · Generated ${fmtDate(data.generatedAt)}`}
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <CountChip label="Unmapped" count={countOf('UNMAPPED')} tone="red" />
                <CountChip label="Inactive but mapped" count={countOf('INACTIVE_MAPPED')} tone="amber" />
                <CountChip label="Duplicate sequence" count={countOf('DUPLICATE_SEQUENCE')} tone="amber" />
              </div>

              {exceptions.length === 0 ? (
                <InfoBanner>No mapping exceptions — every active posting account resolves to a financial-statement group.</InfoBanner>
              ) : (
                <div className="overflow-auto -mx-4 -mb-4">
                  <table className="w-full text-sm border-collapse min-w-[800px]">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className={cn(TH_CLS, 'w-48')}>Type</th>
                        <th className={TH_CLS}>Account</th>
                        <th className={TH_CLS}>Name</th>
                        <th className={TH_CLS}>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exceptions.map((e, i) => (
                        <tr key={`${e.type}-${e.accountCode}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2">
                            <Pill label={EXCEPTION_LABELS[e.type] ?? e.type} tone={EXCEPTION_TONES[e.type] ?? 'grey'} />
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{e.accountCode}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{e.accountName}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{e.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Section>

          {/* Full mapping table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Full Mapping</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {accounts.length.toLocaleString('en-US')} accounts
              </p>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1300px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={TH_CLS}>Account</th>
                    <th className={TH_CLS}>Name</th>
                    <th className={TH_CLS}>Type</th>
                    <th className={TH_CLS}>Posting</th>
                    <th className={TH_CLS}>FS Group</th>
                    <th className={TH_CLS}>Reporting Group</th>
                    <th className={cn(TH_R_CLS, 'w-20')}>Seq</th>
                    <th className={TH_CLS}>IFRS Code</th>
                    <th className={TH_CLS}>Cash-Flow Cat.</th>
                    <th className={cn(TH_CLS, 'w-24')}>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No accounts found for this company.
                      </td>
                    </tr>
                  ) : accounts.map(a => (
                    <tr key={a.accountId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{a.code}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{a.name}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{a.accountType}</td>
                      <td className="px-3 py-2">
                        <Pill label={a.isPostingAccount ? 'Posting' : 'Header'} tone={a.isPostingAccount ? 'blue' : 'grey'} />
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{a.financialStatementGroup ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{a.reportingGroup ?? <Dash />}</td>
                      <td className={cn(AMT_CLS, 'text-gray-600 dark:text-gray-400')}>
                        {a.reportingSequence === null ? <Dash /> : a.reportingSequence}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{a.ifrsMappingCode ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{a.cashFlowCategory ?? <Dash />}</td>
                      <td className="px-3 py-2">
                        <Pill label={a.isActive ? 'Active' : 'Inactive'} tone={a.isActive ? 'green' : 'grey'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
